import React, { useState, useEffect, useContext } from 'react';
import { useFormContext, Controller, get } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../../../../order.schema';
import { FINAL_PRODUCT_FORM_DATA } from '../../../../constants';
import { NumericInput } from '../../../../../../OrderForm/components/NumericInput';
import ApiService from '../../../../../../../../services/ApiService';
import InfiniteSelect from '../../../../../../../../components/InfiniteSelect';
import { FeedbackContext } from '../../../../../../../../contexts/FeedbackContext';

import {
  FormGrid,
  FormGroup,
  Label,
  Select,
} from '../../../../style';

interface FinalProductSectionProps {
  itemPath: ItemPath;
}

const FinalProductSection: React.FC<FinalProductSectionProps> = ({ itemPath }) => {
  const { control, formState: { errors }, setValue, watch } = useFormContext<OrderSchemaType>();
  const { setFeedback } = useContext(FeedbackContext);
  const [formats, setFormats] = useState<{ id: number; name: string }[]>([]);

  const finalProductPath = `${itemPath}.finalProduct` as const;
  const isTopLevelItem = !itemPath.includes('.subItems.');

  const finishingId = watch(`${itemPath}.finishingId` as any) as number | undefined;
  const finishingName = watch(`${itemPath}.finishingName` as any) as string | undefined;

  useEffect(() => {
    if (!isTopLevelItem) return;
    let active = true;
    ApiService.stockLookup.getFormats()
      .then(res => { if (active) setFormats(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (active) setFeedback({ message: 'Não foi possível carregar os formatos do item.', isOpen: true, statusOk: false }); });
    return () => { active = false; };
  }, [isTopLevelItem, setFeedback]);

  // A rota /backend/finishing/list devolve o tenant inteiro de uma vez, sem page nem search:
  // a busca acontece dentro do InfiniteSelect, sobre a lista já carregada.
  const buscarAcabamentos = async () => {
    const res = await ApiService.stockLookup.getFinishings();
    return (Array.isArray(res.data) ? res.data : [])
      .map((f: any) => ({ id: f.id, name: f.name || f.type || '' }))
      .filter((f: { name: string }) => f.name !== '');
  };

  const escolherAcabamento = (opcao: { id: any; name: string } | null) => {
    setValue(`${itemPath}.finishingId` as any, opcao?.id ?? undefined, { shouldDirty: true, shouldValidate: false });
    setValue(`${itemPath}.finishingName` as any, opcao?.name ?? undefined, { shouldDirty: true, shouldValidate: false });
  };

  return (
    <>
      <FormGrid columns={3} style={{ marginTop: '16px' }}>
        <FormGroup>
          <Label htmlFor={`${finalProductPath}.fabricante`}>Fabricante</Label>
          <Controller
            name={`${finalProductPath}.fabricante`}
            control={control}
            render={({ field }) => (
              <Select id={`${finalProductPath}.fabricante`} {...field} hasError={!!get(errors, `${finalProductPath}.fabricante`)}>
                <option value="">Selecione</option>
                {FINAL_PRODUCT_FORM_DATA.manufacturers.map(type => <option key={type} value={type}>{type}</option>)}
              </Select>
            )}
          />
        </FormGroup>
        {isTopLevelItem && (
          <FormGroup>
            <Label htmlFor={`${itemPath}.formatId`}>Formato</Label>
            <Controller
              name={`${itemPath}.formatId` as any}
              control={control}
              render={({ field }) => (
                <Select
                  id={`${itemPath}.formatId`}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  hasError={!!get(errors, `${itemPath}.formatId`)}
                >
                  <option value="">Selecione</option>
                  {formats.map(format => (
                    <option key={format.id} value={format.id}>{format.name}</option>
                  ))}
                </Select>
              )}
            />
          </FormGroup>
        )}
        {isTopLevelItem && (
          <FormGroup>
            <Label htmlFor={`${itemPath}.finishingId`}>Acabamento</Label>
            <InfiniteSelect
              mode="crud"
              value={finishingId ? { id: finishingId, name: finishingName ?? '' } : null}
              onSelect={escolherAcabamento}
              fetchFunction={buscarAcabamentos}
              onItemCreate={async (name: string) => {
                const res = await ApiService.stockLookup.createFinishing({ name: name.trim() });
                return { id: res.data.id, name: res.data.name || res.data.type || '' };
              }}
              allowCreate
              allowDelete={false}
              showCreateOnlyIfNotExists
              minCreateLength={2}
              showPantoneSwatch={false}
              placeholder="Buscar ou criar acabamento"
              hasError={!!get(errors, `${itemPath}.finishingId`)}
            />
          </FormGroup>
        )}
      </FormGrid>

      {!isTopLevelItem && (
        <FormGrid columns={3} style={{ marginTop: '16px' }}>
          <FormGroup>
            <Label htmlFor={`${finalProductPath}.quantidade`}>Quantidade</Label>
            <NumericInput
              id={`${finalProductPath}.quantidade`}
              name={`${finalProductPath}.quantidade`}
              value={(watch(`${finalProductPath}.quantidade` as any) as any) || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(`${finalProductPath}.quantidade` as any, e.target.value, { shouldDirty: true })}
              onBlur={() => {}}
              hasError={!!get(errors, `${finalProductPath}.quantidade`)}
            />
          </FormGroup>
        </FormGrid>
      )}
    </>
  );
};

export default FinalProductSection;
